<script lang="ts">
  type Language = "yaml" | "json" | "properties" | "text";

  let {
    value = $bindable(""),
    language = "text",
    readonly = false,
    ariaLabel = "Редактор конфигурации",
  }: {
    value?: string;
    language?: Language;
    readonly?: boolean;
    ariaLabel?: string;
  } = $props();

  let input = $state<HTMLTextAreaElement>();
  let highlighted = $state<HTMLElement>();
  let caret = $state(0);

  const suggestions = ["true", "false", "on", "off", "null"];
  const currentLine = $derived(value.slice(0, caret).split("\n").pop() ?? "");
  const suggestionMatch = $derived(
    language === "yaml"
      ? currentLine.match(/^(.*:\s*)([A-Za-z]*)$/)
      : language === "properties"
        ? currentLine.match(/^(.*=\s*)([A-Za-z]*)$/)
        : language === "json"
          ? currentLine.match(/^(.*:\s*["']?)([A-Za-z]*)$/)
          : null,
  );
  const currentToken = $derived(suggestionMatch?.[2] ?? "");
  const visibleSuggestions = $derived(
    !readonly && suggestionMatch && currentToken.length > 0
      ? suggestions.filter(
          (item) =>
            item.startsWith(currentToken.toLowerCase()) && item !== currentToken.toLowerCase(),
        )
      : [],
  );

  function escapeHtml(text: string): string {
    return text.replace(
      /[&<>"']/g,
      (character) =>
        ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[character] ??
        character,
    );
  }

  function highlightLine(line: string): string {
    const escaped = escapeHtml(line);
    if (language === "yaml") {
      return escaped
        .replace(/(^\s*-?\s*)([\w.-]+)(:)/, '$1<span class="syntax-key">$2</span>$3')
        .replace(
          /(:\s*)(true|false|on|off|null)(\s*(?:#.*)?$)/gi,
          '$1<span class="syntax-bool">$2</span>$3',
        )
        .replace(/(#[^<]*)$/, '<span class="syntax-comment">$1</span>');
    }
    if (language === "properties") {
      return escaped
        .replace(/^([^=:#]+)(=|:)/, '<span class="syntax-key">$1</span>$2')
        .replace(/(=|:\s*)(true|false|on|off|null)\s*$/i, '$1<span class="syntax-bool">$2</span>')
        .replace(/^(\s*[#!].*)$/, '<span class="syntax-comment">$1</span>');
    }
    if (language === "json") {
      return escaped
        .replace(/(&quot;[^&]*?&quot;)\s*:/g, '<span class="syntax-key">$1</span>:')
        .replace(/\b(true|false|null)\b/g, '<span class="syntax-bool">$1</span>')
        .replace(/(\/\/.*)$/, '<span class="syntax-comment">$1</span>');
    }
    return escaped;
  }

  const highlightedContent = $derived(value.split("\n").map(highlightLine).join("\n"));

  function syncScroll(): void {
    if (!input || !highlighted) {
      return;
    }
    highlighted.scrollTop = input.scrollTop;
    highlighted.scrollLeft = input.scrollLeft;
  }

  function updateCaret(): void {
    caret = input?.selectionStart ?? value.length;
  }

  function insertSuggestion(item: string): void {
    if (!input || !suggestionMatch) {
      return;
    }
    const tokenStart = caret - currentToken.length;
    value = value.slice(0, tokenStart) + item + value.slice(caret);
    caret = tokenStart + item.length;
    requestAnimationFrame(() => {
      input?.focus();
      input?.setSelectionRange(caret, caret);
    });
  }

  function handleKeydown(event: KeyboardEvent): void {
    if (!visibleSuggestions.length || (event.key !== "Tab" && event.key !== "Enter")) {
      return;
    }
    event.preventDefault();
    insertSuggestion(visibleSuggestions[0]);
  }
</script>

<div class="config-editor" class:readonly>
  <div class="editor-viewport">
    <!-- Input is escaped by escapeHtml before fixed syntax spans are inserted. -->
    <!-- eslint-disable-next-line svelte/no-at-html-tags -->
    <pre bind:this={highlighted} aria-hidden="true">{@html highlightedContent}<span
        class="editor-tail">&#32;</span
      ></pre>
    <textarea
      bind:this={input}
      bind:value
      {readonly}
      spellcheck="false"
      wrap="off"
      aria-label={ariaLabel}
      oninput={updateCaret}
      onkeyup={updateCaret}
      onclick={updateCaret}
      onkeydown={handleKeydown}
      onscroll={syncScroll}></textarea>
  </div>
  {#if visibleSuggestions.length}
    <div class="suggestions" role="listbox" aria-label="Автодополнение">
      {#each visibleSuggestions as suggestion (suggestion)}
        <button
          type="button"
          role="option"
          aria-selected="false"
          onclick={() => insertSuggestion(suggestion)}>{suggestion}</button
        >
      {/each}
      <small>Tab или Enter для вставки</small>
    </div>
  {/if}
</div>

<style>
  .config-editor {
    display: flex;
    flex-direction: column;
    min-width: 0;
    overflow: hidden;
    background: var(--ink-deep);
  }
  .editor-viewport {
    position: relative;
    flex: 1 1 19rem;
    min-height: 0;
    overflow: hidden;
    background: var(--ink-deep);
  }
  .config-editor pre,
  .config-editor textarea {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    min-height: 19rem;
    margin: 0;
    padding: 1rem;
    border: 0;
    font:
      400 0.75rem/1.65 ui-monospace,
      SFMono-Regular,
      Menlo,
      Consolas,
      monospace;
    tab-size: 2;
    white-space: pre;
    overflow: auto;
  }
  .config-editor pre {
    pointer-events: none;
    color: #d4e6ca;
  }
  .config-editor textarea {
    z-index: 1;
    resize: none;
    outline: 0;
    color: transparent;
    caret-color: var(--cream);
    background: transparent;
    -webkit-text-fill-color: transparent;
  }
  .config-editor textarea::selection {
    color: transparent;
    background: rgb(240 112 79 / 0.28);
  }
  .config-editor.readonly textarea {
    color: var(--dim);
    -webkit-text-fill-color: var(--dim);
  }
  :global(.syntax-key) {
    color: #e9b86d;
  }
  :global(.syntax-bool) {
    color: #9bdc8f;
  }
  :global(.syntax-comment) {
    color: #77716b;
    font-style: italic;
  }
  .editor-tail {
    color: transparent;
  }
  .suggestions {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.3rem;
    padding: 0.3rem 1rem;
    border-top: 1px solid var(--line-strong);
    background: var(--surface-raised);
  }
  .suggestions button {
    padding: 0.35rem 0.5rem;
    border: 0;
    color: var(--green);
    background: transparent;
    font:
      600 0.72rem ui-monospace,
      monospace;
  }
  .suggestions button:hover {
    color: var(--white);
    background: rgb(154 189 143 / 0.12);
  }
  .suggestions small {
    padding: 0 0.35rem;
    color: var(--dim);
    font-size: 0.58rem;
  }
</style>
